import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { firmInfo } from '../data/mock';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_accounting-pros-7/artifacts/qi69ddhg_New_CA_India_Logo.png" 
                alt="CA India Logo"
                className="h-12 w-auto object-contain bg-white rounded-lg p-1"
              />
              <div>
                <h3 className="font-bold text-lg text-white">{firmInfo.name}</h3>
                <p className="text-xs text-emerald-400">Chartered Accountants</p>
              </div>
            </div>
            <p className="text-gray-400 mb-4 leading-relaxed">
              {firmInfo.taglineHindi}
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              Registered with ICAI | Established {firmInfo.foundedYear}
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Facebook size={18} />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Twitter size={18} />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Linkedin size={18} />
              </a>
              <a
                href="https://www.instagram.com/capeeyushpengoria?igsh=MWo4dnZjazA0MHB6ag=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors"
              >
                <Instagram size={18} />
              </a>

            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 relative">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-emerald-500 -mb-2"></span>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="hover:text-emerald-400 transition-colors flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 relative">
              Our Services
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-emerald-500 -mb-2"></span>
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  Statutory Audit
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  ITR Filing
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  GST Services
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  Company Incorporation
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  TDS Returns
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>
                  Financial Advisory
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6 relative">
              Contact Us
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-emerald-500 -mb-2"></span>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-emerald-500 flex-shrink-0 mt-1" />
                <div>
                  <span className="text-white text-sm font-medium">Head Office:</span>
                  <p className="text-sm">{firmInfo.headOffice}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-emerald-500 flex-shrink-0 mt-1" />
                <div>
                  <span className="text-white text-sm font-medium">Branch Office:</span>
                  <p className="text-sm">{firmInfo.branchOffice}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-emerald-500 flex-shrink-0" />
                <a href={`tel:${firmInfo.phone1}`} className="hover:text-emerald-400 transition-colors">
                  +91-{firmInfo.phone1}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-emerald-500 flex-shrink-0" />
                <a href={`mailto:${firmInfo.email}`} className="hover:text-emerald-400 transition-colors break-all text-sm">
                  {firmInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock size={20} className="text-emerald-500 flex-shrink-0" />
                <span>Mon - Sat: 10:00 AM - 7:00 PM</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {firmInfo.name}. All Rights Reserved.
            </p>
            <p className="text-gray-500 text-sm">
              Chartered Accountants | Registered with ICAI
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/Privacy-Policy" className="text-gray-500 hover:text-emerald-400 transition-colors">Privacy Policy</Link>
              <Link to="/Terms-Of-Use" className="text-gray-500 hover:text-emerald-400 transition-colors">Terms of Use</Link>
              <Link to="/Disclaimer" className="text-gray-500 hover:text-emerald-400 transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
